/**
 * 通信量とリンク使用率の計算（章 6）。
 *
 * 【最重要】ここも出力をハードコードしない。
 * どのリンクがどれだけ混んでいるかは、機器の trafficMbps と物理トポロジから計算する。
 * 感染 PC を隔離すれば使用率は下がり、ping も速くなる。
 *
 * 【乱数を使わない】
 * linkUtilization / topTalkers / detectAnomaly は世界の状態だけから決まる純粋関数。
 * ping の「揺らぎ」だけが既存の rng.ts（固定シード方式）を使う。
 * 機器の走査順は ID の昇順に固定してあるので、Object のキー順に左右されない。
 *
 * 【計算モデル】（docs/design-ch3plus.md 3.2）
 * - 各機器の trafficMbps を、その機器からインターネットまでの経路上の全リンクに足し込む
 * - リンクの容量は両端ポートの speedMbps の小さい方
 * - 使用率 = 合計 / 容量。使用率が高いリンクを通る通信は RTT が増え、ロスが出る
 * - 使用率が 100% を超えたリンクは飽和として扱う
 *
 * 【なぜ「インターネットまで」なのか】
 * 章 6 の障害は「1 台が大量の外向き通信を出し、上流の細いリンクを埋める」こと。
 * 社内サーバー宛の通信まで一般化すると、機器ごとに宛先の内訳を持たせる必要があり、
 * 教えたい核心（**共通して通る場所を見る**）がぼやける。
 * インターネットへの経路に絞れば「全員が通る 1 本」が自然に浮かび上がる。
 */

import type { Device, DeviceId, LinkId, NetworkWorld } from '../types/network';
import { findPath, isDeviceOperational, linksAlongPath } from './topology';

// ---------------------------------------------------------------
// 定数
// ---------------------------------------------------------------

/**
 * 両端ポートの速度が分からない（両方 0 ＝未リンク）ときに使う容量。
 * topology.ts の getLinkStatus と同じ既定値にそろえる。
 */
export const DEFAULT_CAPACITY_MBPS = 100;

/** ここを超えると「混雑」。棒グラフが橙になり、遅延も増え始める */
export const UTILIZATION_WARN_PERCENT = 50;

/** ここを超えると「飽和」。棒グラフが赤になり、パケットが落ち始める */
export const UTILIZATION_SATURATED_PERCENT = 100;

/** 使用率がこの割合までは遅延ゼロ（＝UTILIZATION_WARN_PERCENT と同じ地点） */
export const CONGESTION_FREE_RATIO = UTILIZATION_WARN_PERCENT / 100;

/** 混雑による遅延の係数（ms）。超過分の 2 乗で効く */
export const CONGESTION_MS = 120;

/** 1 ホップあたりの混雑遅延の上限（ms）。青天井にしない */
export const CONGESTION_MAX_MS = 300;

/** ここを超えるとキューが溢れ始める。ちょうど 100% でもロスが出るよう 1.0 より手前に置く */
export const LOSS_THRESHOLD_RATIO = 0.95;

/** 使用率 100% ちょうどのときのキュー溢れによるロス率 */
export const QUEUE_DROP_RATE = 0.08;

/** 混雑によるパケットロス率の上限。全滅はさせない（章 6 は「遅い」であって「切れる」ではない） */
export const MAX_CONGESTION_LOSS = 0.6;

/** 異常判定：上位 1 台が全体に占める割合の下限 */
export const ANOMALY_MIN_SHARE = 0.5;

/** 異常判定：2 位に対する倍率の下限 */
export const ANOMALY_MIN_RATIO = 3;

/** 異常判定：これ未満の通信量は、比率がどうであれ異常とみなさない（Mbps） */
export const ANOMALY_MIN_MBPS = 50;

// ---------------------------------------------------------------
// 型
// ---------------------------------------------------------------

/** 1 本のリンクの混み具合 */
export interface LinkLoad {
  /** 流れている合計 Mbps */
  mbps: number;
  /** 両端ポートの遅い方 */
  capacityMbps: number;
  /** 使用率（%）。100 を超えることがある＝飽和 */
  percent: number;
}

export type LinkUtilizationMap = Record<LinkId, LinkLoad>;

/** 通信を出している機器 1 台 */
export interface TopTalker {
  deviceId: string;
  label: string;
  mbps: number;
}

/**
 * 「明らかに 1 台だけおかしい」と言える状態。
 *
 * **infected フラグは見ない。** 感染しているかどうかは世界の裏側の情報で、
 * プレイヤーが計器から読み取れるものではない。
 * ここでは通信量の偏りだけから判断する（現実の異常検知と同じ根拠にする）。
 */
export interface TrafficAnomaly {
  deviceId: string;
  label: string;
  mbps: number;
  /** 全体の通信量に占める割合（0〜1） */
  share: number;
  /** 2 位の何倍か。2 位が居なければ Infinity */
  ratioToNext: number;
}

export type UtilizationTone = 'ok' | 'warn' | 'saturated';

// ---------------------------------------------------------------
// 小物
// ---------------------------------------------------------------

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/** 使用率から表示上の区分を決める。UI が閾値を自前で持たないようにする */
export function utilizationTone(percent: number): UtilizationTone {
  if (percent >= UTILIZATION_SATURATED_PERCENT) return 'saturated';
  if (percent >= UTILIZATION_WARN_PERCENT) return 'warn';
  return 'ok';
}

// ---------------------------------------------------------------
// 容量
// ---------------------------------------------------------------

/**
 * リンクの容量（Mbps）＝**両端ポートの speedMbps の小さい方**。
 *
 * speedMbps の 0 は「未リンク」を意味する値なので容量としては使わず、
 * 両端とも 0 の場合だけ既定値に落とす（topology.ts の getLinkStatus と同じ規則）。
 * 存在しないリンクは 0。
 */
export function linkCapacityMbps(world: NetworkWorld, linkId: LinkId): number {
  const link = world.links[linkId];
  if (!link) return 0;
  const speeds: number[] = [];
  for (const end of ['a', 'b'] as const) {
    const ref = link[end];
    const port = world.devices[ref.deviceId]?.ports.find((p) => p.index === ref.portIndex);
    if (port && port.speedMbps > 0) speeds.push(port.speedMbps);
  }
  return speeds.length > 0 ? Math.min(...speeds) : DEFAULT_CAPACITY_MBPS;
}

// ---------------------------------------------------------------
// 通信元
// ---------------------------------------------------------------

/** 機器を ID の昇順で列挙する（結果を決定論的にするため） */
function devicesSorted(world: NetworkWorld): Device[] {
  return Object.keys(world.devices)
    .sort()
    .map((id) => world.devices[id]);
}

/** インターネットを表す仮想ノード。複数あれば ID の若い方 */
export function internetDeviceId(world: NetworkWorld): DeviceId | null {
  const found = devicesSorted(world).find((device) => device.kind === 'internet');
  return found?.id ?? null;
}

/**
 * いま実際に通信を出している機器の一覧（ID 昇順）。
 *
 * - **隔離された機器・電源が落ちている機器は数えない。**
 *   隔離した瞬間に使用率が落ちるのが章 6 の手応えになる
 * - インターネット側の仮想ノードは数えない（外から来る通信は扱わない）
 */
export function trafficSources(world: NetworkWorld): TopTalker[] {
  const result: TopTalker[] = [];
  for (const device of devicesSorted(world)) {
    if (device.kind === 'internet') continue;
    if (!isDeviceOperational(device)) continue;
    const mbps = device.trafficMbps;
    if (typeof mbps !== 'number' || !Number.isFinite(mbps) || mbps <= 0) continue;
    result.push({ deviceId: device.id, label: device.label, mbps: round1(mbps) });
  }
  return result;
}

/**
 * 通信量の多い順に並べた機器の一覧（上位通信元）。
 * limit を省略すると全件返す。同量なら ID の昇順で安定させる。
 */
export function topTalkers(world: NetworkWorld, limit?: number): TopTalker[] {
  const sorted = trafficSources(world).sort(
    (a, b) => b.mbps - a.mbps || a.deviceId.localeCompare(b.deviceId),
  );
  if (limit === undefined) return sorted;
  return sorted.slice(0, Math.max(0, limit));
}

/**
 * 「1 台だけ明らかに突出している」かを判定する。
 *
 * 章 6 の学習の肝は **異常な 1 台が一目で分かること**。
 * 判定の根拠を UI ではなくここに置き、単体テストできるようにする。
 *
 * 3 つすべてを満たしたときだけ異常とみなす:
 *   1. 上位 1 台が全体の ANOMALY_MIN_SHARE 以上を占める
 *   2. 2 位の ANOMALY_MIN_RATIO 倍以上ある
 *   3. 絶対量が ANOMALY_MIN_MBPS 以上ある（小さい値の比率で騒がない）
 */
export function detectAnomaly(
  world: NetworkWorld,
  talkers: TopTalker[] = topTalkers(world),
): TrafficAnomaly | null {
  if (talkers.length === 0) return null;
  const top = talkers[0];
  if (top.mbps < ANOMALY_MIN_MBPS) return null;

  const total = talkers.reduce((sum, talker) => sum + talker.mbps, 0);
  if (total <= 0) return null;

  const next = talkers[1]?.mbps ?? 0;
  const share = top.mbps / total;
  const ratioToNext = next > 0 ? top.mbps / next : Number.POSITIVE_INFINITY;

  if (share < ANOMALY_MIN_SHARE) return null;
  if (ratioToNext < ANOMALY_MIN_RATIO) return null;

  return {
    deviceId: top.deviceId,
    label: top.label,
    mbps: top.mbps,
    share: round3(share),
    ratioToNext: Number.isFinite(ratioToNext) ? round1(ratioToNext) : ratioToNext,
  };
}

// ---------------------------------------------------------------
// リンク使用率
// ---------------------------------------------------------------

/**
 * すべてのリンクの使用率。
 *
 * 各機器の trafficMbps を、その機器からインターネットまでの経路上の
 * **全リンク**に足し込む。経路が無い機器（上流が切れている等）は
 * どのリンクにも積まれない。
 *
 * 落ちているリンクも 0 として結果に含める（モニターが行ごと消えないように）。
 */
export function linkUtilization(world: NetworkWorld): LinkUtilizationMap {
  const map: LinkUtilizationMap = {};
  for (const linkId of Object.keys(world.links)) {
    map[linkId] = { mbps: 0, capacityMbps: linkCapacityMbps(world, linkId), percent: 0 };
  }

  const internetId = internetDeviceId(world);
  if (internetId !== null) {
    for (const source of trafficSources(world)) {
      if (source.deviceId === internetId) continue;
      const path = findPath(world, source.deviceId, internetId);
      if (!path) continue;
      for (const linkId of linksAlongPath(world, path)) {
        const entry = map[linkId];
        if (entry) entry.mbps += source.mbps;
      }
    }
  }

  for (const linkId of Object.keys(map)) {
    const entry = map[linkId];
    entry.mbps = round1(entry.mbps);
    entry.percent = entry.capacityMbps > 0 ? round1((entry.mbps / entry.capacityMbps) * 100) : 0;
  }
  return map;
}

/** 1 本のリンクの使用率（%）。リンクが存在しなければ null */
export function linkUtilizationPercent(
  world: NetworkWorld,
  linkId: LinkId,
  load?: LinkUtilizationMap,
): number | null {
  if (!world.links[linkId]) return null;
  const map = load ?? linkUtilization(world);
  return map[linkId]?.percent ?? null;
}

/** いちばん混んでいるリンク。リンクが 1 本も無ければ null */
export function busiestLink(
  world: NetworkWorld,
  load?: LinkUtilizationMap,
): { linkId: LinkId; load: LinkLoad } | null {
  const map = load ?? linkUtilization(world);
  let best: { linkId: LinkId; load: LinkLoad } | null = null;
  // キーの並び順に左右されないよう、同率なら ID の昇順で決める
  for (const linkId of Object.keys(map).sort()) {
    const entry = map[linkId];
    if (best === null || entry.percent > best.load.percent) best = { linkId, load: entry };
  }
  return best;
}

/** すべてのリンクの使用率が指定 % を下回っているか（章 6 のクリア判定） */
export function allLinksUtilizationBelow(
  world: NetworkWorld,
  percent: number,
  load?: LinkUtilizationMap,
): boolean {
  const map = load ?? linkUtilization(world);
  return Object.keys(map).every((linkId) => map[linkId].percent < percent);
}

// ---------------------------------------------------------------
// ping への反映（章 6 の症状は「遅い」であって「つながらない」ではない）
// ---------------------------------------------------------------

/**
 * 使用率から 1 ホップ分の遅延（ms）を求める。
 *
 * 空いている間はゼロ。混み始めてからは待ち行列が伸びるので、
 * 超過分の 2 乗で効かせる（無線の wirelessHopCostMs と同じ考え方）。
 * 上限を置いて、飽和したリンクでも RTT が発散しないようにする。
 */
export function congestionDelayMs(ratio: number): number {
  if (!Number.isFinite(ratio) || ratio <= CONGESTION_FREE_RATIO) return 0;
  const excess = (ratio - CONGESTION_FREE_RATIO) / (1 - CONGESTION_FREE_RATIO);
  return round1(Math.min(CONGESTION_MAX_MS, CONGESTION_MS * excess ** 2));
}

/**
 * 使用率からパケットロス率を求める。
 *
 * 2 つの足し算にする:
 *   - **溢れる分**: 容量を超えた通信は物理的に入りきらない（1 − 1/使用率）
 *   - **キュー溢れ**: 100% 手前からバッファが埋まり始めて少しずつ落ちる
 * 上限は MAX_CONGESTION_LOSS。全部落とすと「つながらない」になってしまい、
 * 章 6 が教えたい「遅い」から外れる。
 */
export function congestionLossRate(ratio: number): number {
  if (!Number.isFinite(ratio) || ratio <= LOSS_THRESHOLD_RATIO) return 0;
  const overflow = ratio > 1 ? 1 - 1 / ratio : 0;
  const queue =
    clamp((ratio - LOSS_THRESHOLD_RATIO) / (1 - LOSS_THRESHOLD_RATIO), 0, 1) * QUEUE_DROP_RATE;
  return round3(clamp(overflow + queue, 0, MAX_CONGESTION_LOSS));
}

/** 混雑によるリンク 1 本分の遅延（ms）。リンクが無ければ 0 */
export function congestionHopCostMs(
  world: NetworkWorld,
  linkId: LinkId | undefined,
  load?: LinkUtilizationMap,
): number {
  if (!linkId) return 0;
  const map = load ?? linkUtilization(world);
  const entry = map[linkId];
  if (!entry) return 0;
  return congestionDelayMs(entry.percent / 100);
}

/**
 * 経路上の混雑から決まるパケットロス率。
 * 複数あれば最も悪いリンクを採る（wirelessLossRateForPath と同じ規則）。
 */
export function congestionLossRateForPath(
  world: NetworkWorld,
  path: DeviceId[],
  load?: LinkUtilizationMap,
): number {
  if (path.length <= 1) return 0;
  const map = load ?? linkUtilization(world);
  let worst = 0;
  for (const linkId of linksAlongPath(world, path)) {
    const entry = map[linkId];
    if (!entry) continue;
    worst = Math.max(worst, congestionLossRate(entry.percent / 100));
  }
  return worst;
}
